/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { PropTypes } from 'prop-types';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { nanoid } from 'nanoid';
import ImageGallery from 'react-image-gallery';
import { Modal } from 'react-bootstrap';
import ContentCard from '../../../common/ContentCard';
import BoundingBox from '../../../common/BoundingBox';
import { PICTURE_LABELS } from '../../Segments/SegmentPhotos/constants';
import { ICONS_LABELS } from '../../Segments/SegmentPhotos/constants';
import SegmentIntroIcon from '../../../icons/horizonIcon.svg';
import LeftNav from '../../../icons/arrowLeft.svg';
import RightNav from '../../../icons/arrowRight.svg';
import CloseIcon from '../../../icons/close-normal.svg';
import DeleteIcon from '../../../icons/trash-filled.svg';
import { deleteImage } from '../../../api';
import './styles.scss';

/**
 * PhotoGallery Component
 * classification enum: [BIN, LITTER, HOTSPOT]
 * @param photos Gallery array, for example: [{ photoUrl: "", classification: enum }]
 * @param photoBreakdown Counts by litter type, for example: { segmentIntros: 0, litters: 0, bins: 0, hotspots: 0 }
 */
export default function PhotoGallery({ photos, photoBreakdown }) {
    const [showGallery, setShowGallery] = useState(false);
    const [currentIndex, setCurrentIndexGallery] = useState(0);

    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarData, setSnackbarData] = useState({
        status: 'success',
        text: '',
    });

    const [deletedImageIds, setDeletedImageIds] = useState({});

    const classifications = [
        {
            icon: SegmentIntroIcon,
            classification: PICTURE_LABELS.SEGMENT_INTRO,
            title: 'Horizon Photo(s)',
            type: 'segmentIntros',
        },
        {
            icon: ICONS_LABELS['LITTER'],
            classification: PICTURE_LABELS.LITTER,
            title: 'Litter Photo(s)',
            type: 'litters',
        },
        {
            icon: ICONS_LABELS['BIN'],
            classification: PICTURE_LABELS.BIN,
            title: 'Bin Photo(s)',
            type: 'bins',
        },
        {
            icon: ICONS_LABELS['HOTSPOT'],
            classification: PICTURE_LABELS.HOTSPOT,
            title: 'Hotspot Photo(s)',
            type: 'hotspots',
        },
    ];

    function renderDeleteIcon(imageId) {
        console.log('renderDeleteIcon', imageId);
        return (
            <img
                onClick={(e) => onDelete(e, imageId)}
                src={DeleteIcon}
                className="delete-action" // declared in styles.scss
            />
        );
    }

    function onDelete(e, imageId) {
        console.log('onDelete', imageId);
        e.preventDefault();
        e.stopPropagation();

        if (window.confirm('Are you sure you want to delete this image?')) {
            doDelete(imageId);
        }
    }

    async function doDelete(imageId) {
        try {
            const result = await deleteImage(imageId);
            console.log('doDelete result', result);

            // provide a success or failure snackbar message based on result
            setShowSnackbar(true);
            if (result) {
                setSnackbarData({
                    status: 'success',
                    text: 'Successfully deleted photo!',
                });
                setDeletedImageIds((curr) => {
                    const obj = { ...curr };
                    obj[imageId] = true;
                    return obj;
                });
            } else {
                setSnackbarData({
                    status: 'error',
                    text: 'Failed to delete photo',
                });
            }
        } catch (error) {
            console.log('doDelete error', error);

            setShowSnackbar(true);
            setSnackbarData({
                status: 'error',
                text: `Failed to delete photo${
                    error ? JSON.stringify(error) : '!'
                }`,
            });
        }
    }

    return (
        <ContentCard
            heading={`Segment Photos - ${photos.length || 0}`}
            content={
                <>
                    <div className="col-12">
                        {photos &&
                            classifications &&
                            classifications.map((group, groupIndex) => (
                                <div
                                    key={group.title}
                                    className="classification"
                                >
                                    <div>
                                        <img src={group.icon} />
                                        <label>{group.title}</label>
                                        <label className="classificationCount">
                                            {photoBreakdown[group.type] || 0}
                                        </label>
                                    </div>
                                    <div>
                                        {photos
                                            .filter(
                                                (photo) =>
                                                    photo &&
                                                    photo.classification &&
                                                    photo.classification ==
                                                        group.classification &&
                                                    !deletedImageIds[
                                                        photo.imageId
                                                    ],
                                            )
                                            .map((photo) => (
                                                <span
                                                    key={nanoid()}
                                                    className="thumbnail"
                                                >
                                                    <img
                                                        className="image"
                                                        src={photo.photoUrl}
                                                        onClick={() => {
                                                            setCurrentIndexGallery(
                                                                photo.photoIndex,
                                                            );
                                                            setShowGallery(
                                                                true,
                                                            );
                                                        }}
                                                    />
                                                    {group.classification ===
                                                    PICTURE_LABELS.LITTER
                                                        ? renderDeleteIcon(
                                                              photo.imageId,
                                                          )
                                                        : null}
                                                </span>
                                            ))}
                                    </div>
                                </div>
                            ))}
                        {photos && (
                            <Modal
                                show={showGallery}
                                onHide={() => {
                                    setCurrentIndexGallery(0);
                                    setShowGallery(false);
                                }}
                                aria-labelledby="simple-modal-title"
                                aria-describedby="simple-modal-description"
                                className="segment-image-gallery-modal"
                                centered={true}
                                animation={false}
                            >
                                <img
                                    src={CloseIcon}
                                    onClick={() => {
                                        setCurrentIndexGallery(0);
                                        setShowGallery(false);
                                    }}
                                    className="close-gallery"
                                />
                                <ImageGallery
                                    showBullets={false}
                                    showThumbnails={false}
                                    showPlayButton={false}
                                    showFullscreenButton={false}
                                    showNav={true}
                                    autoPlay={false}
                                    infinite={false}
                                    renderItem={(item) => {
                                        return (
                                            <BoundingBox
                                                image={item.original}
                                                boxes={
                                                    item.posData
                                                        ? item.posData.map(
                                                              (pos) => [
                                                                  pos.x1,
                                                                  pos.y1,
                                                                  pos.x2 -
                                                                      pos.x1,
                                                                  pos.y2 -
                                                                      pos.y1,
                                                              ],
                                                          )
                                                        : []
                                                }
                                            />
                                        );
                                    }}
                                    items={photos.map((photo) => ({
                                        original: photo ? photo.photoUrl : '',
                                        photoUrl: photo ? photo.photoUrl : '',
                                        originalClass: 'carousal-image',
                                        posData: photo ? photo.posData : [],
                                    }))}
                                    renderLeftNav={(onClick, disabled) => {
                                        return (
                                            <input
                                                type="image"
                                                src={LeftNav}
                                                disabled={disabled}
                                                onClick={onClick}
                                                className="image-gallery-icon image-gallery-left-nav image-gallery-custom-nav"
                                            />
                                        );
                                    }}
                                    renderRightNav={(onClick, disabled) => {
                                        return (
                                            <input
                                                type="image"
                                                src={RightNav}
                                                disabled={disabled}
                                                onClick={onClick}
                                                className="image-gallery-icon image-gallery-right-nav image-gallery-custom-nav"
                                            />
                                        );
                                    }}
                                    startIndex={currentIndex}
                                />
                            </Modal>
                        )}
                    </div>
                    {/* Snackbar pop up for delete image feedback */}
                    <Snackbar
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        open={showSnackbar}
                        autoHideDuration={6000}
                        onClose={() => setShowSnackbar(false)}
                    >
                        <Alert
                            onClose={() => setShowSnackbar(false)}
                            severity={snackbarData.status || 'success'}
                            sx={{ width: '100%' }}
                        >
                            {snackbarData.text || ''}
                        </Alert>
                    </Snackbar>
                </>
            }
        />
    );
}

PhotoGallery.propTypes = {
    photos: PropTypes.array,
    photoBreakdown: PropTypes.object,
};
